"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Download, Share, X } from "lucide-react";

export default function InstallPrompt() {
  const pathname = usePathname();
  const [isReady, setIsReady] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const [isDebug, setIsDebug] = useState(false);

  useEffect(() => {
    // Hide on desktop
    if (window.innerWidth > 768) {
      setIsDesktop(true);
    }

    // Check if already installed
    const isStandaloneMode = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone;
    setIsStandalone(!!isStandaloneMode);

    // Detect iOS (including iPadOS 13+)
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    // Pick up early event from layout.tsx
    if ((window as any).__deferredPrompt) {
      setDeferredPrompt((window as any).__deferredPrompt);
    }

    // Listen for Android install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      (window as any).__deferredPrompt = e;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    
    // Check if previously dismissed
    const hasDismissed = localStorage.getItem("nabd_pwa_dismissed");
    
    // Also support forcing for debug
    const debugMode = window.location.search.includes("debug_pwa=1");
    setIsDebug(debugMode);

    if (hasDismissed && !debugMode) {
      setDismissed(true);
    }

    setIsReady(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("nabd_pwa_dismissed", "true");
  };

  const handleInstallClick = async () => {
    if (isIOS || (!deferredPrompt && isDebug)) {
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setDeferredPrompt(null);
      }
    }
  };

  if (!isReady || isDesktop || (isStandalone && !isDebug) || (dismissed && !isDebug)) {
    return null;
  }

  if (pathname?.startsWith("/login")) {
    return null;
  }

  // Only show if we are on iOS OR if Android has fired the install prompt event
  if (!isIOS && !deferredPrompt && !isDebug) {
    return null;
  }

  return (
    <div className="pwa-overlay">
      <div className="pwa-card animation-slide-down">
        
        <button onClick={handleDismiss} className="pwa-close">
          <X size={16} />
        </button>

        {!showIOSInstructions ? (
          <div className="pwa-content">
            <img src="/icon-192x192.png" alt="Plexa App" className="pwa-icon" />
            <div className="pwa-text">
              <h3>تطبيق بليكسا (Plexa)</h3>
              <p>ثبّت التطبيق للوصول السريع والإشعارات</p>
            </div>
            <button onClick={handleInstallClick} className="pwa-install-btn">
              <Download size={14} />
              تثبيت
            </button>
          </div>
        ) : (
          <div className="pwa-ios-instructions">
            <h3>لتثبيت التطبيق على الآيفون:</h3>
            <ol dir="rtl">
              <li>
                <span className="pwa-step">1</span>
                اضغط على زر المشاركة <Share size={16} className="pwa-share-icon" /> في أسفل المتصفح.
              </li>
              <li>
                <span className="pwa-step">2</span>
                اختر <strong>"الإضافة إلى الصفحة الرئيسية"</strong> (Add to Home Screen).
              </li>
              <li>
                <span className="pwa-step">3</span>
                اضغط على <strong>"إضافة"</strong> (Add).
              </li>
            </ol>
            <button onClick={handleDismiss} className="pwa-ok-btn">
              حسناً، فهمت
            </button>
          </div>
        )}

        <style jsx>{`
          .pwa-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            z-index: 99999;
            padding: 16px;
            pointer-events: none;
            display: flex;
            justify-content: center;
          }
          .pwa-card {
            width: 100%;
            max-width: 400px;
            background: #0d1117;
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
            padding: 16px;
            pointer-events: auto;
            position: relative;
            direction: rtl;
          }
          .pwa-close {
            position: absolute;
            top: 8px;
            left: 8px;
            background: rgba(255, 255, 255, 0.05);
            border: none;
            color: rgba(255, 255, 255, 0.4);
            border-radius: 50%;
            padding: 4px;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .pwa-close:hover {
            color: #fff;
            background: rgba(255, 255, 255, 0.1);
          }
          .pwa-content {
            display: flex;
            align-items: center;
            gap: 16px;
          }
          .pwa-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
          }
          .pwa-text {
            flex: 1;
          }
          .pwa-text h3 {
            color: #fff;
            font-size: 15px;
            font-weight: 700;
            margin: 0 0 2px 0;
          }
          .pwa-text p {
            color: rgba(255, 255, 255, 0.6);
            font-size: 12px;
            margin: 0;
          }
          .pwa-install-btn {
            background: #7c3aed;
            color: #fff;
            border: none;
            border-radius: 12px;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            box-shadow: 0 0 15px rgba(124, 58, 237, 0.3);
            transition: background 0.2s;
          }
          .pwa-install-btn:hover {
            background: #c084fc;
          }
          .pwa-ios-instructions {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 8px;
          }
          .pwa-ios-instructions h3 {
            color: #fff;
            font-size: 15px;
            font-weight: 700;
            margin: 0 0 12px 0;
          }
          .pwa-ios-instructions ol {
            color: rgba(255, 255, 255, 0.7);
            font-size: 13px;
            text-align: right;
            width: 100%;
            padding: 0;
            margin: 0;
            list-style: none;
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .pwa-ios-instructions li {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .pwa-step {
            background: rgba(255, 255, 255, 0.1);
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            flex-shrink: 0;
          }
          .pwa-share-icon {
            display: inline;
            margin: 0 4px;
            color: #c084fc;
          }
          .pwa-ok-btn {
            margin-top: 16px;
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
            border: none;
            border-radius: 12px;
            padding: 8px 24px;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            transition: background 0.2s;
          }
          .pwa-ok-btn:hover {
            background: rgba(255, 255, 255, 0.15);
          }
          .animation-slide-down {
            animation: slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}
